import { hueAngle } from '@limonade/color-science';
import { HueDistributionAnalyzer, LuminanceDistributionAnalyzer, SaturationDistributionAnalyzer } from './distributions.js';
import { ToneCurveMatcher } from './ToneCurveMatcher.js';
const round = (value, digits = 4) => Number(value.toFixed(digits));
const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
const COLOR_VECTORS = [
    { vector: 'red', targetHue: 0 },
    { vector: 'yellow', targetHue: 60 },
    { vector: 'green', targetHue: 120 },
    { vector: 'cyan', targetHue: 180 },
    { vector: 'blue', targetHue: 240 },
    { vector: 'magenta', targetHue: 300 },
];
function weightedMean(samples, channel) {
    const total = samples.reduce((sum, sample) => sum + (sample.weight ?? 1), 0);
    return total ? samples.reduce((sum, sample) => sum + sample[channel] * (sample.weight ?? 1), 0) / total : 0;
}
function sampleLuma(sample) {
    return sample.r * 0.2126 + sample.g * 0.7152 + sample.b * 0.0722;
}
function sampleSaturation(sample) {
    const maximum = Math.max(sample.r, sample.g, sample.b);
    const minimum = Math.min(sample.r, sample.g, sample.b);
    return maximum > 1e-6 ? (maximum - minimum) / maximum : 0;
}
function circularHueDistance(first, second) {
    return Math.abs((((first - second + 540) % 360) - 180));
}
function circularHueDelta(target, source) {
    return ((target - source + 540) % 360) - 180;
}
function weightedCircularMean(entries) {
    if (!entries.length)
        return null;
    let x = 0;
    let y = 0;
    let total = 0;
    for (const entry of entries) {
        const radians = entry.hue * Math.PI / 180;
        x += Math.cos(radians) * entry.weight;
        y += Math.sin(radians) * entry.weight;
        total += entry.weight;
    }
    if (total <= 1e-8)
        return null;
    return (Math.atan2(y / total, x / total) * 180 / Math.PI + 360) % 360;
}
function normalizedWhite(rgb) {
    const mean = Math.max(1e-5, (rgb.r + rgb.g + rgb.b) / 3);
    return [rgb.r / mean, rgb.g / mean, rgb.b / mean];
}
function isSkinToneLike(sample) {
    const maximum = Math.max(sample.r, sample.g, sample.b);
    const minimum = Math.min(sample.r, sample.g, sample.b);
    const luma = sampleLuma(sample);
    const sat = sampleSaturation(sample);
    const hue = hueAngle(sample);
    return luma >= 0.22 && luma <= 0.78 &&
        hue >= 4 && hue <= 62 &&
        sat >= 0.10 && sat <= 0.68 &&
        sample.r >= sample.g * 0.88 &&
        sample.g >= sample.b * 0.62 &&
        maximum - minimum >= 0.035;
}
function estimateSkinTone(samples, semantic = false) {
    const candidates = samples
        .map((sample) => ({
        sample,
        luma: sampleLuma(sample),
        saturation: sampleSaturation(sample),
        hue: hueAngle(sample),
    }))
        .filter((entry) => semantic
        ? entry.luma >= 0.035 && entry.luma <= 0.95 && (entry.sample.weight ?? 1) > 0
        : isSkinToneLike(entry.sample))
        .sort((first, second) => {
        const firstMidtone = 1 - Math.abs(first.luma - 0.48) / 0.48;
        const secondMidtone = 1 - Math.abs(second.luma - 0.48) / 0.48;
        const firstHueScore = semantic ? 0 : 1 - Math.abs(first.hue - 28) / 40;
        const secondHueScore = semantic ? 0 : 1 - Math.abs(second.hue - 28) / 40;
        return (secondMidtone + secondHueScore * 0.55) - (firstMidtone + firstHueScore * 0.55);
    });
    const selected = candidates.slice(0, Math.max(8, Math.min(384, Math.ceil(samples.length * 0.16))));
    if (!selected.length) {
        return {
            r: 0,
            g: 0,
            b: 0,
            sampleCount: 0,
            confidence: 0,
        };
    }
    let totalWeight = 0;
    let r = 0;
    let g = 0;
    let b = 0;
    let skinLikelihood = 0;
    for (const entry of selected) {
        const midtoneWeight = clamp(1 - Math.abs(entry.luma - 0.48) / 0.55, 0.15, 1);
        const hueWeight = semantic ? 1 : clamp(1 - Math.abs(entry.hue - 28) / 48, 0.2, 1);
        const satWeight = semantic ? 1 : clamp(1 - Math.abs(entry.saturation - 0.34) / 0.55, 0.25, 1);
        const weight = (entry.sample.weight ?? 1) * midtoneWeight * hueWeight * satWeight;
        totalWeight += weight;
        r += entry.sample.r * weight;
        g += entry.sample.g * weight;
        b += entry.sample.b * weight;
        skinLikelihood += hueWeight * satWeight * weight;
    }
    const sampleCountConfidence = clamp(selected.length / Math.max(24, Math.min(220, samples.length * 0.08)), 0, 1);
    const skinConfidence = totalWeight ? clamp(skinLikelihood / totalWeight, 0, 1) : 0;
    return {
        r: round(r / Math.max(1e-6, totalWeight), 5),
        g: round(g / Math.max(1e-6, totalWeight), 5),
        b: round(b / Math.max(1e-6, totalWeight), 5),
        sampleCount: selected.length,
        confidence: round(sampleCountConfidence * 0.42 + skinConfidence * 0.58),
    };
}
function estimateWhitePoint(samples) {
    const ranked = samples
        .map((sample) => ({
        sample,
        luma: sampleLuma(sample),
        saturation: sampleSaturation(sample),
        clipped: Math.max(sample.r, sample.g, sample.b) > 0.985,
    }))
        .filter((entry) => entry.luma >= 0.42 && entry.saturation <= 0.32)
        .sort((first, second) => {
        const firstScore = first.luma * 1.15 - first.saturation * 0.75 - (first.clipped ? 0.12 : 0);
        const secondScore = second.luma * 1.15 - second.saturation * 0.75 - (second.clipped ? 0.12 : 0);
        return secondScore - firstScore;
    });
    const fallback = samples
        .map((sample) => ({
        sample,
        luma: sampleLuma(sample),
        saturation: sampleSaturation(sample),
        clipped: Math.max(sample.r, sample.g, sample.b) > 0.995,
    }))
        .filter((entry) => entry.luma >= 0.25 && entry.saturation <= 0.5)
        .sort((first, second) => {
        const firstScore = first.luma - first.saturation * 0.45 - (first.clipped ? 0.1 : 0);
        const secondScore = second.luma - second.saturation * 0.45 - (second.clipped ? 0.1 : 0);
        return secondScore - firstScore;
    });
    const selected = (ranked.length >= 8 ? ranked : fallback)
        .slice(0, Math.max(8, Math.min(256, Math.ceil(samples.length * 0.12))));
    if (!selected.length) {
        return {
            r: 1,
            g: 1,
            b: 1,
            sampleCount: 0,
            confidence: 0,
        };
    }
    let totalWeight = 0;
    let r = 0;
    let g = 0;
    let b = 0;
    let neutrality = 0;
    for (const entry of selected) {
        const weight = (entry.sample.weight ?? 1) * clamp(entry.luma, 0.15, 1.25) * (1 - clamp(entry.saturation, 0, 0.65) * 0.85);
        totalWeight += weight;
        r += entry.sample.r * weight;
        g += entry.sample.g * weight;
        b += entry.sample.b * weight;
        neutrality += (1 - clamp(entry.saturation / 0.5, 0, 1)) * weight;
    }
    const sampleCountConfidence = clamp(selected.length / Math.max(24, Math.min(180, samples.length * 0.06)), 0, 1);
    const neutralityConfidence = totalWeight ? clamp(neutrality / totalWeight, 0, 1) : 0;
    const brightnessConfidence = clamp(selected.reduce((sum, entry) => sum + entry.luma, 0) / selected.length / 0.75, 0, 1);
    return {
        r: round(r / Math.max(1e-6, totalWeight), 5),
        g: round(g / Math.max(1e-6, totalWeight), 5),
        b: round(b / Math.max(1e-6, totalWeight), 5),
        sampleCount: selected.length,
        confidence: round(sampleCountConfidence * 0.36 + neutralityConfidence * 0.44 + brightnessConfidence * 0.2),
    };
}
function estimateBlackPoint(samples) {
    const ranked = samples
        .map((sample) => ({
        sample,
        luma: sampleLuma(sample),
        saturation: sampleSaturation(sample),
    }))
        .filter((entry) => entry.luma <= 0.28 && entry.saturation <= 0.55)
        .sort((first, second) => {
        const firstScore = (1 - first.luma) * 1.2 - first.saturation * 0.5;
        const secondScore = (1 - second.luma) * 1.2 - second.saturation * 0.5;
        return secondScore - firstScore;
    });
    const selected = ranked.slice(0, Math.max(8, Math.min(256, Math.ceil(samples.length * 0.12))));
    if (!selected.length) {
        return {
            r: 0,
            g: 0,
            b: 0,
            sampleCount: 0,
            confidence: 0,
        };
    }
    let totalWeight = 0;
    let r = 0;
    let g = 0;
    let b = 0;
    let neutrality = 0;
    for (const entry of selected) {
        const weight = (entry.sample.weight ?? 1) * clamp(1 - entry.luma, 0.2, 1) * (1 - clamp(entry.saturation, 0, 0.8) * 0.45);
        totalWeight += weight;
        r += entry.sample.r * weight;
        g += entry.sample.g * weight;
        b += entry.sample.b * weight;
        neutrality += (1 - clamp(entry.saturation / 0.75, 0, 1)) * weight;
    }
    const sampleCountConfidence = clamp(selected.length / Math.max(24, Math.min(180, samples.length * 0.06)), 0, 1);
    const neutralityConfidence = totalWeight ? clamp(neutrality / totalWeight, 0, 1) : 0;
    const shadowConfidence = clamp((0.34 - selected.reduce((sum, entry) => sum + entry.luma, 0) / selected.length) / 0.34, 0, 1);
    return {
        r: round(r / Math.max(1e-6, totalWeight), 5),
        g: round(g / Math.max(1e-6, totalWeight), 5),
        b: round(b / Math.max(1e-6, totalWeight), 5),
        sampleCount: selected.length,
        confidence: round(sampleCountConfidence * 0.34 + neutralityConfidence * 0.36 + shadowConfidence * 0.3),
    };
}
function estimateWhitePointMatch(reference, source) {
    const referenceWhite = estimateWhitePoint(reference);
    const sourceWhite = estimateWhitePoint(source);
    const referenceNormalized = normalizedWhite(referenceWhite);
    const sourceNormalized = normalizedWhite(sourceWhite);
    const channelGains = [
        clamp(referenceNormalized[0] / Math.max(1e-5, sourceNormalized[0]), 0.72, 1.38),
        clamp(referenceNormalized[1] / Math.max(1e-5, sourceNormalized[1]), 0.72, 1.38),
        clamp(referenceNormalized[2] / Math.max(1e-5, sourceNormalized[2]), 0.72, 1.38),
    ].map((value) => round(value, 6));
    const gainSpread = Math.max(...channelGains) - Math.min(...channelGains);
    const confidence = Math.min(referenceWhite.confidence, sourceWhite.confidence) * clamp(1 - gainSpread * 0.28, 0.45, 1);
    return {
        reference: referenceWhite,
        source: sourceWhite,
        channelGains,
        confidence: round(confidence),
    };
}
function estimateBlackPointMatch(reference, source) {
    const referenceBlack = estimateBlackPoint(reference);
    const sourceBlack = estimateBlackPoint(source);
    const channelOffsets = [
        clamp(referenceBlack.r - sourceBlack.r, -0.12, 0.12),
        clamp(referenceBlack.g - sourceBlack.g, -0.12, 0.12),
        clamp(referenceBlack.b - sourceBlack.b, -0.12, 0.12),
    ].map((value) => round(value, 6));
    const offsetSpread = Math.max(...channelOffsets) - Math.min(...channelOffsets);
    const confidence = Math.min(referenceBlack.confidence, sourceBlack.confidence) * clamp(1 - offsetSpread * 1.25, 0.35, 1);
    return {
        reference: referenceBlack,
        source: sourceBlack,
        channelOffsets,
        confidence: round(confidence),
    };
}
function estimateSaturationMatch(referenceSaturationMean, sourceSaturationMean) {
    const ratio = clamp(referenceSaturationMean / Math.max(0.025, sourceSaturationMean), 0.45, 1.75);
    const signalStrength = clamp(Math.max(referenceSaturationMean, sourceSaturationMean) / 0.35, 0, 1);
    const confidence = signalStrength * clamp(1 - Math.abs(ratio - 1) * 0.22, 0.55, 1);
    return {
        referenceMean: round(referenceSaturationMean, 5),
        sourceMean: round(sourceSaturationMean, 5),
        saturationRatio: round(ratio, 6),
        confidence: round(confidence),
    };
}
function estimateSkinToneMatch(reference, source, semantic = false) {
    const referenceSkin = estimateSkinTone(reference, semantic);
    const sourceSkin = estimateSkinTone(source, semantic);
    const referenceNormalized = normalizedWhite(referenceSkin);
    const sourceNormalized = normalizedWhite(sourceSkin);
    const channelPower = [
        clamp(1 - (referenceNormalized[0] - sourceNormalized[0]) * 0.42, 0.84, 1.16),
        clamp(1 - (referenceNormalized[1] - sourceNormalized[1]) * 0.42, 0.84, 1.16),
        clamp(1 - (referenceNormalized[2] - sourceNormalized[2]) * 0.42, 0.84, 1.16),
    ].map((value) => round(value, 6));
    const powerSpread = Math.max(...channelPower) - Math.min(...channelPower);
    const confidence = Math.min(referenceSkin.confidence, sourceSkin.confidence) * clamp(1 - powerSpread * 0.55, 0.4, 1);
    return {
        reference: referenceSkin,
        source: sourceSkin,
        channelPower,
        confidence: round(confidence),
    };
}
function vectorHueMean(samples, targetHue, tonal = false) {
    const entries = [];
    for (const sample of samples) {
        const sat = sampleSaturation(sample);
        const luma = sampleLuma(sample);
        if (sat < 0.16 || (!tonal && (luma < 0.08 || luma > 0.96)) || (sample.weight ?? 1) <= 0)
            continue;
        const hue = hueAngle(sample);
        const distance = circularHueDistance(hue, targetHue);
        if (distance > 38)
            continue;
        const weight = (sample.weight ?? 1) * sat * clamp(1 - distance / 42, 0.08, 1) * clamp(1 - Math.abs(luma - 0.5) / 0.7, 0.35, 1);
        entries.push({ hue, weight, support: Math.min(1, sample.weight ?? 1), distance, saturation: sat });
    }
    entries.sort((first, second) => second.weight - first.weight);
    const selected = entries.slice(0, Math.min(320, Math.max(24, Math.ceil(samples.length * 0.08))));
    const hue = weightedCircularMean(selected);
    const meanDistance = selected.length
        ? selected.reduce((sum, entry) => sum + entry.distance, 0) / selected.length
        : 38;
    const meanSaturation = selected.length
        ? selected.reduce((sum, entry) => sum + entry.saturation, 0) / selected.length
        : 0;
    const effectiveCount = selected.reduce((sum, entry) => sum + entry.support, 0);
    const countConfidence = clamp(effectiveCount / Math.max(16, Math.min(160, samples.length * 0.04)), 0, 1);
    const hueConfidence = clamp(1 - meanDistance / 38, 0, 1);
    const saturationConfidence = clamp(meanSaturation / 0.45, 0, 1);
    return {
        hue: hue === null ? null : round(hue, 4),
        count: selected.length,
        confidence: effectiveCount < 8 ? 0 : round(countConfidence * 0.42 + hueConfidence * 0.34 + saturationConfidence * 0.24),
    };
}
function estimateSixVectorColorMatch(reference, source, tonal = false) {
    const vectors = COLOR_VECTORS.map(({ vector, targetHue }) => {
        const referenceVector = vectorHueMean(reference, targetHue, tonal);
        const sourceVector = vectorHueMean(source, targetHue, tonal);
        const confidence = Math.min(referenceVector.confidence, sourceVector.confidence);
        const rawShift = referenceVector.hue === null || sourceVector.hue === null
            ? 0
            : circularHueDelta(referenceVector.hue, sourceVector.hue);
        const hueShiftDegrees = confidence >= 0.16 ? clamp(rawShift, -28, 28) : 0;
        return {
            vector,
            targetHue,
            referenceHue: referenceVector.hue,
            sourceHue: sourceVector.hue,
            hueShiftDegrees: round(hueShiftDegrees, 4),
            referenceSampleCount: referenceVector.count,
            sourceSampleCount: sourceVector.count,
            confidence: round(confidence),
        };
    });
    const hueVsHueCurve = Array.from({ length: 12 }, (_, index) => {
        const hue = index * 30;
        let weightedShift = 0;
        let totalWeight = 0;
        for (const vector of vectors) {
            const distance = circularHueDistance(hue, vector.targetHue);
            if (distance > 60)
                continue;
            const weight = clamp(1 - distance / 60, 0, 1) * vector.confidence;
            weightedShift += vector.hueShiftDegrees * weight;
            totalWeight += weight;
        }
        return round(totalWeight ? clamp(weightedShift / totalWeight, -24, 24) : 0);
    });
    const confidence = vectors.reduce((sum, vector) => sum + vector.confidence, 0) / vectors.length;
    return {
        vectors,
        hueVsHueCurve,
        confidence: round(confidence),
    };
}
export class CorrectionEstimator {
    hue = new HueDistributionAnalyzer(12);
    saturation = new SaturationDistributionAnalyzer();
    luminance = new LuminanceDistributionAnalyzer();
    toneCurve = new ToneCurveMatcher();
    /** Shared six-vector estimator, also used with soft tonal sample weights. */
    estimateSixVectorColorMatch(reference, source, tonal = false) {
        return estimateSixVectorColorMatch(reference, source, tonal);
    }
    /** Explicitly segmented skin is not filtered again by a fixed skin hue range. */
    estimateSkinToneMatch(reference, source, semantic = false) {
        return estimateSkinToneMatch(reference, source, semantic);
    }
    estimate(reference, source) {
        const referenceHue = this.hue.analyze(reference);
        const sourceHue = this.hue.analyze(source);
        const referenceSaturation = this.saturation.analyze(reference);
        const sourceSaturation = this.saturation.analyze(source);
        const referenceLuminance = this.luminance.analyze(reference);
        const sourceLuminance = this.luminance.analyze(source);
        const whitePoint = estimateWhitePointMatch(reference, source);
        const blackPoint = estimateBlackPointMatch(reference, source);
        const saturationMatch = estimateSaturationMatch(referenceSaturation.mean, sourceSaturation.mean);
        const skinToneMatch = estimateSkinToneMatch(reference, source);
        const colorVectorMatch = estimateSixVectorColorMatch(reference, source);
        const referenceR = weightedMean(reference, 'r');
        const referenceG = weightedMean(reference, 'g');
        const referenceB = weightedMean(reference, 'b');
        const sourceR = weightedMean(source, 'r');
        const sourceG = weightedMean(source, 'g');
        const sourceB = weightedMean(source, 'b');
        const whitePointTemperatureShift = ((whitePoint.channelGains[0] - whitePoint.channelGains[2]) * 82);
        const whitePointTintShift = ((whitePoint.channelGains[1] - (whitePoint.channelGains[0] + whitePoint.channelGains[2]) / 2) * 92);
        const whitePointWeight = clamp(whitePoint.confidence, 0, 0.82);
        const globalTemperatureShift = ((referenceR - referenceB) - (sourceR - sourceB)) * 100;
        const temperatureShift = globalTemperatureShift * (1 - whitePointWeight) + whitePointTemperatureShift * whitePointWeight;
        const referenceTint = referenceG - (referenceR + referenceB) / 2;
        const sourceTint = sourceG - (sourceR + sourceB) / 2;
        const globalTintShift = (referenceTint - sourceTint) * 100;
        const tintShift = globalTintShift * (1 - whitePointWeight) + whitePointTintShift * whitePointWeight;
        const exposureShift = Math.log2(Math.max(1e-4, referenceLuminance.mean) / Math.max(1e-4, sourceLuminance.mean));
        const saturationShift = (referenceSaturation.mean - sourceSaturation.mean) * 100;
        const contrastShift = (referenceLuminance.standardDeviation - sourceLuminance.standardDeviation) * 200;
        const hueVsHue = referenceHue.histogram.map((_, index) => {
            const referenceWeight = referenceHue.histogram[index] ?? 0;
            const sourceWeight = sourceHue.histogram[index] ?? 0;
            return round(clamp((referenceWeight - sourceWeight) * 60, -30, 30));
        });
        const hueSatReference = new Array(12).fill(0);
        const hueSatSource = new Array(12).fill(0);
        const hueCountsReference = new Array(12).fill(0);
        const hueCountsSource = new Array(12).fill(0);
        const collect = (samples, totals, counts) => {
            for (const sample of samples) {
                const index = Math.min(11, Math.floor(hueAngle(sample) / 30));
                const maximum = Math.max(sample.r, sample.g, sample.b);
                const minimum = Math.min(sample.r, sample.g, sample.b);
                totals[index] = (totals[index] ?? 0) + (maximum ? (maximum - minimum) / maximum : 0);
                counts[index] = (counts[index] ?? 0) + 1;
            }
        };
        collect(reference, hueSatReference, hueCountsReference);
        collect(source, hueSatSource, hueCountsSource);
        const hueVsSat = hueSatReference.map((total, index) => round(clamp((total / Math.max(1, hueCountsReference[index] ?? 0) -
            (hueSatSource[index] ?? 0) / Math.max(1, hueCountsSource[index] ?? 0)) * 100, -50, 50)));
        const hueVsHueCurve = hueVsHue.map((value, index) => {
            const vectorValue = colorVectorMatch.hueVsHueCurve[index] ?? 0;
            const vectorWeight = clamp(colorVectorMatch.confidence * 0.9, 0, 0.9);
            return round(clamp(value * (1 - vectorWeight) + vectorValue * vectorWeight, -30, 30));
        });
        return {
            temperatureShift: round(clamp(temperatureShift, -100, 100)),
            tintShift: round(clamp(tintShift, -100, 100)),
            exposureShift: round(clamp(exposureShift, -4, 4)),
            saturationShift: round(clamp(saturationShift, -100, 100)),
            contrastShift: round(clamp(contrastShift, -100, 100)),
            toneCurve: this.toneCurve.match(referenceLuminance.histogram, sourceLuminance.histogram),
            hueVsHue: hueVsHueCurve,
            hueVsSat,
            whitePoint,
            blackPoint,
            saturationMatch,
            skinToneMatch,
            colorVectorMatch,
        };
    }
}
//# sourceMappingURL=CorrectionEstimator.js.map